import 'leaflet/dist/leaflet.css';
import './style.css';
import { getIdTokenClaims, isAuthenticated, logout, redirectToLogin } from './auth/auth';
import { MOCK_MODE } from './config';
import { initialsFromName } from './lib/format';
import * as adminPage from './pages/admin';
import * as callbackPage from './pages/callback';
import * as deviceCheckinsPage from './pages/deviceCheckins';
import * as mapPage from './pages/map';
import * as peoplePage from './pages/people';
import { navigate as routerNavigate, setNavigateListener } from './router';

type PageModule = { mount: (root: HTMLElement) => () => void };

const routes: Record<string, PageModule> = {
  '/': mapPage,
  '/people': peoplePage,
  '/admin': adminPage,
  '/callback': callbackPage,
};

const CHECKINS_ROUTE = /^\/(?:people|admin)\/devices\/([^/]+)\/checkins$/;

const THEME_KEY = 'ft_theme';

const view = document.getElementById('view')!;
const nav = document.getElementById('main-nav')!;
const themeToggle = document.getElementById('theme-toggle') as HTMLButtonElement;
const userMenu = document.getElementById('user-menu')!;
const userMenuBtn = document.getElementById('user-menu-btn') as HTMLButtonElement;
const userDropdown = document.getElementById('user-dropdown')!;
const logoutBtn = document.getElementById('logout-btn') as HTMLButtonElement;
const versionEl = document.getElementById('app-version')!;
const avatarEl = document.getElementById('user-avatar')!;

versionEl.textContent = `v${__APP_VERSION__}`;

let cleanup: (() => void) | null = null;

function currentPath(): string {
  return window.location.pathname;
}

function setActiveNav(path: string): void {
  nav.querySelectorAll<HTMLButtonElement>('button[data-route]').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.route === path);
  });
}

function applyTheme(theme: string): void {
  document.documentElement.setAttribute('data-theme', theme);
  themeToggle.setAttribute('aria-label', theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme');
  localStorage.setItem(THEME_KEY, theme);
  window.dispatchEvent(new CustomEvent('ft-theme-change', { detail: theme }));
}

function closeUserMenu(): void {
  userDropdown.hidden = true;
  userMenuBtn.setAttribute('aria-expanded', 'false');
}

function toggleUserMenu(): void {
  const willOpen = userDropdown.hidden;
  userDropdown.hidden = !willOpen;
  userMenuBtn.setAttribute('aria-expanded', String(willOpen));
}

function initAvatar(): void {
  const claims = getIdTokenClaims();
  const name = (claims?.name as string | undefined)?.trim();
  const email = (claims?.email as string | undefined) ?? null;
  const initials = name ? initialsFromName(name) : email ? email.slice(0, 2).toUpperCase() : 'FT';
  avatarEl.textContent = initials;
}

async function render(): Promise<void> {
  const path = currentPath();

  if (path !== '/callback' && !MOCK_MODE && !isAuthenticated()) {
    await redirectToLogin();
    return;
  }

  cleanup?.();
  cleanup = null;

  if (path !== '/callback') initAvatar();

  const checkinsMatch = path.match(CHECKINS_ROUTE);
  if (checkinsMatch) {
    setActiveNav('/people');
    userMenu.hidden = MOCK_MODE;
    cleanup = deviceCheckinsPage.mount(view, decodeURIComponent(checkinsMatch[1]));
    return;
  }

  const page = routes[path] ?? routes['/'];
  setActiveNav(path in routes ? path : '/');
  userMenu.hidden = MOCK_MODE || path === '/callback';

  cleanup = page.mount(view);
}

nav.addEventListener('click', (e) => {
  const target = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-route]');
  if (!target) return;
  routerNavigate(target.dataset.route!);
});

themeToggle.addEventListener('click', () => {
  const current = document.documentElement.getAttribute('data-theme') ?? 'dark';
  applyTheme(current === 'dark' ? 'light' : 'dark');
});

userMenuBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  toggleUserMenu();
});
document.addEventListener('click', (e) => {
  if (!userMenu.contains(e.target as Node)) closeUserMenu();
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeUserMenu();
});

logoutBtn.addEventListener('click', () => logout());

applyTheme(localStorage.getItem(THEME_KEY) ?? 'dark');

setNavigateListener(render);
window.addEventListener('popstate', () => render());

render();
